import React, { useState, useEffect, useRef, useCallback } from "react";
import toast, { Toaster } from "react-hot-toast";
import config from "../config";
import Navbar from "../components/Navbar";
import GuessGrid from "../components/GuessGrid";
import Keyboard from "../components/Keyboard";

const WORD_LENGTH = 6;
const MAX_GUESSES = 7;

function LettroDaily() {
  const didInit = useRef(false);
  const [answer, setAnswer] = useState("");
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [status, setStatus] = useState("playing");
  const [wordList, setWordList] = useState([]);
  const [validWords, setValidWords] = useState(new Set());

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    async function fetchData() {
      try {
        const wordListId = "wordList-v2";
        const cachedWords = localStorage.getItem(wordListId);
        let wordList;

        if (cachedWords) {
          wordList = JSON.parse(cachedWords);
        } else {
          const wordRes = await fetch(`${config.apiBaseUrl}/words`);
          if (!wordRes.ok) throw new Error("Failed to fetch words");
          const wordListData = await wordRes.json();
          wordList = wordListData.map((w) => w.word);
          localStorage.setItem(wordListId, JSON.stringify(wordList));
        }

        const answerRes = await fetch(`${config.apiBaseUrl}/word-of-the-day`);
        if (!answerRes.ok) throw new Error("Failed to fetch answer");
        const answerData = await answerRes.json();
        setAnswer(answerData.word.toUpperCase());
        const upperWords = wordList.map((w) => w.toUpperCase());
        setWordList(upperWords);
        setValidWords(new Set(wordList));
      } catch (err) {
        console.error("Error loading data:", err);
        toast.error("Could not load game data.");
      }
    }
    fetchData();
  }, []);

  const submitGuess = useCallback(() => {
    if (currentGuess.length !== WORD_LENGTH) {
      toast.error(`Guess must be ${WORD_LENGTH} letters.`);
      return;
    }
    if (!validWords.has(currentGuess.toUpperCase())) {
      toast.error("Word not in list.");
      return;
    }
    const newGuesses = [...guesses, currentGuess];
    setGuesses(newGuesses);
    setCurrentGuess("");

    if (currentGuess === answer) {
      setStatus("won");
      toast("Congrats! See you tomorrow!", { icon: "🎉" });
      let audio = new Audio('sounds/success.mp3');
      audio.play();
    } else if (newGuesses.length >= MAX_GUESSES) {
      setStatus("lost");
      toast("Game over! The word was: " + answer, { icon: "😞" });
    }
  }, [currentGuess, guesses, validWords, answer]);

  const onKeyDown = useCallback(
    (e) => {
      if (status !== "playing") return;
      if (e.key === "Enter") submitGuess();
      else if (e.key === "Backspace") setCurrentGuess((g) => g.slice(0, -1));
      else if (/^[a-zA-Z]$/.test(e.key)) {
        if (currentGuess.length < WORD_LENGTH) {
          setCurrentGuess((g) => g + e.key.toUpperCase());
        }
      }
    },
    [currentGuess, status, submitGuess]
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  const getFeedback = (guess) => {
    const result = Array(WORD_LENGTH).fill("absent");
    const answerChars = answer.split("");
    const guessChars = guess.split("");

    for (let i = 0; i < WORD_LENGTH; i++) {
      if (guessChars[i] === answerChars[i]) {
        result[i] = "correct";
        answerChars[i] = null;
      }
    }

    for (let i = 0; i < WORD_LENGTH; i++) {
      if (result[i] === "correct") continue;
      const idx = answerChars.indexOf(guessChars[i]);
      if (idx !== -1) {
        result[i] = "present";
        answerChars[idx] = null;
      }
    }
    return result;
  };

  const keyboardState = guesses.reduce((acc, guess) => {
    const feedback = getFeedback(guess);
    for (let i = 0; i < WORD_LENGTH; i++) {
      const letter = guess[i];
      if (feedback[i] === "correct") acc[letter] = "correct";
      else if (feedback[i] === "present" && acc[letter] !== "correct") acc[letter] = "present";
      else if (!acc[letter]) acc[letter] = "absent";
    }
    return acc;
  }, {});

  const keyboardRows = [
    "QWERTYUIOP".split(""),
    "ASDFGHJKL".split(""),
    ["Enter", ..."ZXCVBNM".split(""), "Backspace"],
  ];

  const onScreenKeyClick = (key) => {
    if (status !== "playing") return;
    if (key === "Enter") submitGuess();
    else if (key === "Backspace") setCurrentGuess((g) => g.slice(0, -1));
    else if (/^[A-Z]$/.test(key)) {
      if (currentGuess.length < WORD_LENGTH) {
        setCurrentGuess((g) => g + key);
      }
    }
  };

  if (!answer || wordList.length === 0) {
    return (
      <>
        <Navbar gameLogo="/lettro.png" label="Daily" />
        <Toaster position="top-center" />
        <main className="game-container"><p>Loading...</p></main>
      </>
    );
  }

  document.title = "Plaay.nl · Lettro Daily Game";
  return (
    <>
      <Navbar gameLogo="/lettro.png" label="Daily" />
      <Toaster position="top-center" />
      <main className="game-container">
        <GuessGrid
          guesses={guesses}
          currentGuess={currentGuess}
          getFeedback={getFeedback}
          wordLength={WORD_LENGTH}
          maxGuesses={MAX_GUESSES}
        />
        {status === "playing" && <p>Type your guess and press Enter</p>}
        {status === "won" && <p>🎉 Congrats! You guessed the word: {answer}</p>}
        {status === "lost" && <p>😞 Game over! The word was: {answer}</p>}
        <Keyboard
          keyboardRows={keyboardRows}
          keyboardState={keyboardState}
          onKeyClick={onScreenKeyClick}
        />
      </main>
    </>
  );
}

export default LettroDaily;